import React, { useState, useEffect } from 'react';
import { SearchIcon, EyeIcon, CheckIcon, XIcon } from '@heroicons/react/outline';
import { toastService } from '../../../services/toast';
import { ordersApi } from '../../../services/api/orders.api';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';

const BookOrders = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState('all');
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [reviewedOrders, setReviewedOrders] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      // جلب طلبات الكتب
      const data = await ordersApi.getAllOrders('book');

      if (!Array.isArray(data)) {
        // تنسيق بيانات غير صحيح
        toastService.error('تنسيق البيانات غير صحيح');
        return;
      }

      // تحويل البيانات لتطابق الشكل المطلوب
      const formattedData = data.map(order => ({
        ...order,
        amount: order.total || order.amount, // يستخدم total أو amount
        userName: order.trainee || order.userName || (order.user && order.user.name) || 'غير معروف',
        bookTitle: order.bookTitle || (order.book && order.book.title) || 'غير معروف'
      }));

      // البيانات بعد التنسيق

      // فلترة الطلبات حسب الحالة
      const pending = formattedData.filter(order => !order.status || order.status === 'pending');
      const reviewed = formattedData.filter(order => order.status === 'accepted' || order.status === 'rejected');
      
      // الطلبات المعلقة والمراجعة

      setPendingOrders(pending);
      setReviewedOrders(reviewed);
    } catch (error) {
      // خطأ في جلب الطلبات
      toastService.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (orderId) => {
    setIsLoading(true);
    try {
      await ordersApi.updateOrderStatus(orderId, 'approved', 'book');
      toastService.success('تم قبول الطلب بنجاح');
      fetchOrders();
    } catch (error) {
      toastService.error('حدث خطأ أثناء قبول الطلب');
      // خطأ في قبول الطلب
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (orderId) => {
    setIsLoading(true);
    try {
      await ordersApi.updateOrderStatus(orderId, 'rejected', 'book');
      toastService.success('تم رفض الطلب بنجاح');
      fetchOrders();
    } catch (error) {
      toastService.error('حدث خطأ أثناء رفض الطلب');
      // خطأ في رفض الطلب
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewReceipt = (receiptUrl) => {
    if (receiptUrl) {
      setSelectedReceipt(receiptUrl);
    } else {
      toastService.error('لا يوجد إيصال متاح');
    }
  };

  const filterOrders = (orders) => {
    return orders.filter(order => {
      const matchesSearch = searchQuery === '' || 
        order.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.bookTitle.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      const orderDate = new Date(order.created_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);

      const monthAgo = new Date(today);
      monthAgo.setDate(monthAgo.getDate() - 30);

      switch (selectedDate) {
        case 'today':
          return orderDate >= today;
        case 'yesterday':
          return orderDate >= yesterday && orderDate < today;
        case 'week':
          return orderDate >= weekAgo;
        case 'month':
          return orderDate >= monthAgo;
        default:
          return true;
      }
    });
  };

  const filteredPendingOrders = filterOrders(pendingOrders);
  const filteredReviewedOrders = filterOrders(reviewedOrders);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // دالة موحدة لتنسيق التواريخ في التطبيق
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleConfirmDelete = async () => {
    if (!selectedOrder) return;
    
    setIsLoading(true);
    try {
      // حذف من الباك إند
      await ordersApi.deleteOrder(selectedOrder.id, 'book')
      // ثم تحديث الحالة المحلية
      if (selectedOrder.status) {
        setReviewedOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
      } else {
        setPendingOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
      }
      toastService.success('تم حذف الطلب بنجاح');
    } catch (error) {
      toastService.error('حدث خطأ أثناء حذف الطلب');
    } finally {
      setIsLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedOrder(null);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getCurrentItems = (items) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const currentItems = getCurrentItems(
    activeTab === 'pending' ? filteredPendingOrders : filteredReviewedOrders
  );

  const totalPages = Math.ceil(
    (activeTab === 'pending' ? filteredPendingOrders.length : filteredReviewedOrders.length) / itemsPerPage
  );

  return (
    <>
      <div className="p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* بطاقة البحث والفلترة */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
            {/* رأس البطاقة */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">البحث والفلترة</h1>
            </div>

            {/* قسم الفلتر والبحث */}
            <div className="px-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    البحث
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="ابحث باسم المشتري أو الكتاب..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="input-base pr-10"
                    />
                    <SearchIcon className="absolute left-3 top-2 h-5 w-5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    تاريخ الطلب
                  </label>
                  <select
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="input-base"
                  >
                    <option value="all">All Dates</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* بطاقة جدول الطلبات */}
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
            {/* رأس البطاقة */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h1 className="text-lg font-medium text-gray-900 dark:text-white">
                {activeTab === 'pending' ? 'تحت المراجعة' : 'تمت المراجعة'}
              </h1>
              <div className="flex space-x-4 space-x-reverse">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md min-w-[120px] ${
                    activeTab === 'pending'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  تحت المراجعة ({filteredPendingOrders.length})
                </button>
                <button
                  onClick={() => setActiveTab('reviewed')}
                  className={`flex items-center justify-center px-6 py-2 text-sm font-medium rounded-md min-w-[120px] ${
                    activeTab === 'reviewed'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  تمت المراجعة ({filteredReviewedOrders.length})
                </button>
              </div>
            </div>

            {/* الجدول */}
            <div className="overflow-x-auto">
              {currentItems.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        رقم الطلب
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        اسم المشتري
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        الكتاب
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        السعر
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        تاريخ الطلب
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        الإيصال
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map(order => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {order.id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {order.userName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {order.bookTitle}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100 ltr">
                            {(order.total || order.amount || 0).toLocaleString()} ريال
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100 ltr">
                            {formatDate(order.date || order.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {order.paymentMethod === 'online' ? (
                              <span className="text-green-600">دفع إلكتروني</span>
                            ) : (
                              <button
                                onClick={() => handleViewReceipt(order.receipt)}
                                className="text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex justify-center space-x-2 space-x-reverse">
                            {activeTab === 'pending' ? (
                              <>
                                <button
                                  onClick={() => handleApprove(order.id)}
                                  className="text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                                  title="قبول"
                                >
                                  <CheckIcon className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => handleReject(order.id)}
                                  className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                                  title="رفض"
                                >
                                  <XIcon className="h-5 w-5" />
                                </button>
                              </>
                            ) : (
                              <span className={`text-sm ${
                                order.status === 'accepted' 
                                  ? 'text-green-600 dark:text-green-400' 
                                  : 'text-red-600 dark:text-red-400'
                              }`}>
                                {order.status === 'accepted' ? 'مقبول' : 'مرفوض'}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                    <SearchIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    لا توجد نتائج
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    لم يتم العثور على أي طلبات تطابق معايير البحث
                  </p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && currentItems.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400'
                    }`}
                  >
                    السابق
                  </button>
                  <div className="flex items-center gap-2">
                    {[...Array(totalPages)].map((_, index) => (
                      <button
                        key={index + 1}
                        onClick={() => handlePageChange(index + 1)}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${
                          currentPage === index + 1
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md text-sm font-medium ${
                      currentPage === totalPages
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400'
                    }`}
                  >
                    التالي
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* مودال عرض الإيصال */}
      {selectedReceipt && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 cursor-pointer"
          onClick={() => setSelectedReceipt(null)}
        >
          <div className="relative p-4">
            <button
              onClick={() => setSelectedReceipt(null)}
              className="absolute top-2 left-2 text-white bg-black bg-opacity-50 rounded-full p-1 hover:bg-opacity-75 focus:outline-none z-10"
            >
              <XIcon className="h-6 w-6" />
            </button>
            <img 
              src={selectedReceipt} 
              alt="إيصال التحويل" 
              className="max-w-[90vw] max-h-[90vh] object-scale-down rounded-lg"
              onClick={(e) => e.stopPropagation()} 
            />
          </div>
        </div>
      )}

      {/* مودال الحذف */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isLoading) {
            setIsDeleteModalOpen(false);
            setSelectedOrder(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        itemType="الطلب"
        itemTitle={selectedOrder?.userName}
        isLoading={isLoading}
      />
    </>
  );
};

export default BookOrders;
